import { z } from 'zod';
import { AgentChatModelSchema, agentProtocolSchema, AgentTypeEnum, agentTypeSchema } from '../enums/agent.js';
import { StatusTypeSchema } from '../enums/status.js';
import { agentProtocolDataSchema, agentProtocolDetailsDataSchema } from './agent.protocol.js';
import { ComplianceConfigSchema } from './compliance.js';

export const agentSkillSchema = z.object({
  identifier: z.string(),
  name: z.string(),
  description: z.string(),
  examples: z.array(z.string()),
  tags: z.array(z.string()),
});
export type AgentSkill = z.infer<typeof agentSkillSchema>;

export const customAgentDataSchema = z.object({});

export const csvAgentDetailsDataSchema = z.object({
  prompt: z.string().optional(),
});
export const orchestrationAgentDetailsDataSchema = z.object({
  model: AgentChatModelSchema.prefault('gpt-4.1'),
  streamResponse: z.boolean().prefault(false),
});

export const subAgentTableItemSchema = z.object({
  id: z.coerce.number(),
  identifier: z.string(),
  name: z.string(),
  description: z.string(),
  protocol: agentProtocolSchema.nullable(),
  typeId: agentTypeSchema.prefault(AgentTypeEnum.Default),
});
export type SubAgentTableItem = z.infer<typeof subAgentTableItemSchema>;

const agentFileTableItemSchema = z.object({
  id: z.coerce.number(),
  identifier: z.string(),
  originalName: z.string(),
});

const baseAgentSchema = z.object({
  id: z.coerce.number(),
  identifier: z.string(),
  description: z.string(),
  inputSchema: z.record(z.string(), z.unknown()),
  name: z.string(),
  status: StatusTypeSchema,
  createdOn: z.coerce.date(),
  files: z.array(agentFileTableItemSchema).prefault([]),
  systemPrompt: z.string().nullable(),
  systemPromptId: z.uuid().nullable().prefault(null),
  complianceConfig: ComplianceConfigSchema.optional().nullable(),
  tags: z.array(z.string()).prefault([]),
});

export const customAgentSchema = baseAgentSchema.extend(
  z.object({
    data: customAgentDataSchema,
    typeId: z.literal(AgentTypeEnum.Default),
    protocol: agentProtocolDataSchema,
  }).shape
);
export const customAgentDetailsSchema = baseAgentSchema.extend(
  z.object({
    data: customAgentDataSchema,
    typeId: z.literal(AgentTypeEnum.Default),
    protocol: agentProtocolDetailsDataSchema,
  }).shape
);

export const csvAgentSchema = baseAgentSchema.extend(
  z.object({
    data: csvAgentDetailsDataSchema,
    typeId: z.literal(AgentTypeEnum.CSVAgent),
    protocol: agentProtocolDataSchema,
  }).shape
);
export const csvAgentDetailSchema = baseAgentSchema.extend(
  z.object({
    data: csvAgentDetailsDataSchema,
    typeId: z.literal(AgentTypeEnum.CSVAgent),
    protocol: agentProtocolDetailsDataSchema,
  }).shape
);

export const orchestrationAgentSchema = baseAgentSchema.extend(
  z.object({
    data: orchestrationAgentDetailsDataSchema,
    typeId: z.literal(AgentTypeEnum.Orchestration),
    protocol: agentProtocolDataSchema,
  }).shape
);
export const orchestrationAgentDetailSchema = baseAgentSchema.extend(
  z.object({
    data: orchestrationAgentDetailsDataSchema,
    typeId: z.literal(AgentTypeEnum.Orchestration),
    protocol: agentProtocolDetailsDataSchema,
  }).shape
);

export const agentSchema = z.discriminatedUnion('typeId', [
  customAgentSchema,
  csvAgentSchema,
  orchestrationAgentSchema,
]);

export const agentDetailsSchema = z.discriminatedUnion('typeId', [
  customAgentDetailsSchema,
  csvAgentDetailSchema,
  orchestrationAgentDetailSchema,
]);

export type Agent = z.infer<typeof agentSchema>;
export type CustomAgent = z.infer<typeof customAgentSchema>;
export type CsvAgent = z.infer<typeof csvAgentSchema>;
export type OrchestrationAgent = z.infer<typeof orchestrationAgentSchema>;

export const formWorkflowAgentConfigSchema = z.object({
  enableAutoExtraction: z.boolean(),
  enableInteractiveMode: z.boolean(),
  autoSelectOnSingleMatch: z.boolean(),
  maxCandidatesToShow: z.number(),
  contentLengthThreshold: z.number(),
  customPrompts: z
    .object({
      systemPrompt: z.string().optional(),
      matchingPrompt: z.string().optional(),
      extractionPrompt: z.string().optional(),
    })
    .optional(),
});

export const formWorkflowContextSchema = z.object({
  brandIdentifier: z.string(),
  localUserId: z.number(),
  physicianName: z.string().nullable(),
});

export const contextSpecificConfigSchema = z.object({
  global: formWorkflowAgentConfigSchema.partial(),
  kol: formWorkflowAgentConfigSchema.partial(),
  standalone: formWorkflowAgentConfigSchema.partial(),
});

export type AgentDetails = z.infer<typeof agentDetailsSchema>;
export type CustomAgentDetails = z.infer<typeof customAgentDetailsSchema>;
export type CsvAgentDetails = z.infer<typeof csvAgentDetailSchema>;
export type OrchestrationAgentDetails = z.infer<typeof orchestrationAgentDetailSchema>;

export type FormWorkflowAgentConfig = z.infer<typeof formWorkflowAgentConfigSchema>;
export type FormWorkflowContext = z.infer<typeof formWorkflowContextSchema>;
export type ContextSpecificConfig = z.infer<typeof contextSpecificConfigSchema>;
